import momentTimezone from 'moment-timezone';

import { DateFormatConfig } from '../config/formatConfig';
import { MOMENT_CONSTANTS } from '../config/constants';
import { getBrowserTimezone } from '../config/userTimezone';
import { moment, formattedDate } from '../translateDate';
import type { ServerTiming } from '../types';

const unixM = (epoch: number, tz?: string) =>
  tz ? momentTimezone.unix(epoch).tz(tz) : moment.unix(epoch);

const getNow = (): moment.Moment => {
  const serverTime = (window as Window & { serverTiming?: ServerTiming }).serverTiming
    ?.currentServerTime;
  return serverTime ? moment.unix(serverTime) : moment();
};

export const getDueDateProps = (
  args: {
    dueOn: number;
    dueOnDateInUserTimezone?: string;
  },
  showTimeFormat = true,
  tz?: string
) => {
  const { dueOn, dueOnDateInUserTimezone } = args;
  const dueOnM = unixM(dueOn, tz);
  const nowM = getNow();
  const dateFormat = showTimeFormat
    ? `${DateFormatConfig.GENERAL_DATE_FORMAT}, ${DateFormatConfig.TIME_FORMAT}`
    : DateFormatConfig.GENERAL_DATE_FORMAT;
  const formattedDueDate = dueOnDateInUserTimezone
    ? moment(dueOnDateInUserTimezone).format(dateFormat)
    : dueOnM.format(dateFormat);
  const duePast = nowM.diff(dueOnM) > 0;
  const dueDiffInDays = Math.abs(nowM.diff(dueOnM, MOMENT_CONSTANTS.DAYS));
  const dueDiffInHours = Math.abs(nowM.diff(dueOnM, MOMENT_CONSTANTS.HOURS));
  return { formattedDueDate, duePast, dueDiffInDays, dueDiffInHours };
};

export const getCompletedStatusProps = (
  args: {
    completedOn: number;
    timeoutOnDateInUserTimezone?: string;
    completedOnInUserTimezone?: string;
  },
  tz?: string
) => {
  const { completedOn, timeoutOnDateInUserTimezone, completedOnInUserTimezone } = args;
  const dateFormat = `${DateFormatConfig.ABBR_MONTH_DAY}, ${DateFormatConfig.TIME_FORMAT}`;

  let formattedCompletedOn: string;
  if (timeoutOnDateInUserTimezone) {
    formattedCompletedOn = moment(timeoutOnDateInUserTimezone).format(dateFormat);
  } else if (completedOnInUserTimezone) {
    formattedCompletedOn = moment(completedOnInUserTimezone).format(dateFormat);
  } else {
    formattedCompletedOn = unixM(completedOn, tz).format(dateFormat);
  }
  return { formattedCompletedOn };
};

export const getResetStatusProps = (adminResetOn: number) => {
  const nowM = getNow();
  const m = moment.unix(adminResetOn);
  return {
    resetDiffInDays: Math.abs(nowM.diff(m, MOMENT_CONSTANTS.DAYS)),
    resetDiffInHours: Math.abs(nowM.diff(m, MOMENT_CONSTANTS.HOURS)),
  };
};

export const getLastUpdatedProps = (lastUpdatedOn: number) => ({
  diffInDays: getNow().diff(moment.unix(lastUpdatedOn), MOMENT_CONSTANTS.DAYS),
});

export const getAssignedProps = (assignedOn: number) => ({
  assignedDiffInDays: Math.abs(getNow().diff(moment.unix(assignedOn), MOMENT_CONSTANTS.DAYS)),
});

export const getScheduledProps = (scheduledOn: number) => ({
  formattedScheduledOn: moment.unix(scheduledOn).format(DateFormatConfig.ABBR_MONTH_DAY),
});

export const getCoachingScheduledProps = (scheduledOn: number) => ({
  formattedScheduledOn: moment
    .unix(scheduledOn)
    .format(`${DateFormatConfig.ABBR_MONTH_DAY}, ${DateFormatConfig.TIME_FORMAT}`),
});

export const getSessionProps = (
  args: {
    sessionOn: number;
    startTimeInUserTimezone?: string;
  },
  tz?: string
) => ({
  formattedSessionOn: args.startTimeInUserTimezone
    ? moment(args.startTimeInUserTimezone).format(DateFormatConfig.ABBR_MONTH_DAY)
    : unixM(args.sessionOn, tz).format(DateFormatConfig.ABBR_MONTH_DAY),
});

export const getMeetingDateProps = (meetingDate: string) => ({
  formattedMeetingDate: moment(meetingDate).format(DateFormatConfig.GENERAL_DATE_FORMAT),
});

export const getCertificateExpiringOnProps = (certificateExpiringOn: number) => ({
  certificateExpired: moment.unix(certificateExpiringOn).diff(getNow()) < 0,
});

export const getCertExpiryDateProps = (args: {
  certificateExpiringOn: number;
  certificateExpiringOnInUserTimezone?: string;
}) => {
  const { certificateExpiringOn, certificateExpiringOnInUserTimezone } = args;
  const dateFormat = `${DateFormatConfig.ABBR_MONTH_DAY}, ${DateFormatConfig.TIME_FORMAT}`;
  const certificateExpired = moment.unix(certificateExpiringOn).diff(getNow()) < 0;
  const formattedExpiry = certificateExpiringOnInUserTimezone
    ? moment(certificateExpiringOnInUserTimezone).format(dateFormat)
    : moment.unix(certificateExpiringOn).format(dateFormat);
  return { certificateExpired, formattedExpiry };
};

export const getILTSessionProps = (
  args: {
    sessionOn: number;
    startTimeInUserTimezone?: string;
  },
  timezone?: string
) => {
  const browserTz = getBrowserTimezone();
  const dateFormat = `${DateFormatConfig.ABBR_MONTH_DAY}, ${DateFormatConfig.TIME_FORMAT}`;

  if (args.startTimeInUserTimezone) {
    const sessionTz = timezone || browserTz;
    const formatted = moment(args.startTimeInUserTimezone).format(dateFormat);
    const offset = momentTimezone().tz(sessionTz).format('Z');
    return { formattedSession: `${formatted}, GMT ${offset}` };
  }

  const formattedSession = momentTimezone
    .unix(args.sessionOn)
    .tz(browserTz)
    .format(`${dateFormat}, [GMT] Z`);
  return { formattedSession };
};

export const getTimerProps = (timeoutOn: number) => ({
  remainingSeconds: moment.unix(timeoutOn).diff(getNow(), 'second'),
});

export const getCertificateProps = (args: {
  certifiedDate?: string;
  expirationDate?: string;
  recertification?: { start: string; end: string };
}) => {
  const { certifiedDate, expirationDate, recertification } = args;
  const formattedCertifiedDate = certifiedDate
    ? moment.utc(certifiedDate).local().format(DateFormatConfig.GENERAL_DATE_FORMAT)
    : '';
  const formattedExpiryDate = expirationDate
    ? moment.utc(expirationDate).local().format(DateFormatConfig.GENERAL_DATE_FORMAT)
    : '';
  const isInRecertificationPeriod = recertification
    ? moment().utc().isBetween(recertification.start, recertification.end)
    : false;
  return { formattedCertifiedDate, formattedExpiryDate, isInRecertificationPeriod };
};

export const getChallengeInfoProps = (challengeAvailableFrom: number) => {
  const availableFrom = moment.unix(challengeAvailableFrom);
  const nowM = getNow();
  const challengeUpcoming = nowM.isBefore(availableFrom);
  const [dateTo, dateFrom] = challengeUpcoming ? [availableFrom, nowM] : [nowM, availableFrom];
  const diffInDays = dateTo.diff(dateFrom, MOMENT_CONSTANTS.DAYS);
  const diffInHours = dateTo.diff(dateFrom, MOMENT_CONSTANTS.HOURS);
  const isToday = dateTo.date() === dateFrom.date();
  return { challengeUpcoming, diffInDays, diffInHours, isToday };
};

export const dateHelper = {
  dueDate: getDueDateProps,
  completedStatus: getCompletedStatusProps,
  resetStatus: getResetStatusProps,
  lastUpdated: getLastUpdatedProps,
  assigned: getAssignedProps,
  scheduled: getScheduledProps,
  coachingScheduled: getCoachingScheduledProps,
  session: getSessionProps,
  meetingDate: getMeetingDateProps,
  certExpiry: getCertificateExpiringOnProps,
  challengeInfo: getChallengeInfoProps,
  iltSession: getILTSessionProps,
  certExpiryDate: getCertExpiryDateProps,
  certificate: getCertificateProps,
  timer: getTimerProps,
  assetActivity: formattedDate,
};
